import React, { useState } from 'react';
import { ChatMessage } from '../../types';
import { useChatWidgetContext } from '../../ChatWidgetContext';
import { ChatMarkdownContent } from './ChatMarkdownContent';
import ContextChipList from './ContextChipList';
import styles from '../ChatWidget.module.scss';
import nekstAiIcon from './nekstAiIcon.png';

interface MessageBubbleProps {
  message: ChatMessage;
  isGrouped?: boolean;
  isActiveToolCall?: boolean;
}

function formatTime(date: Date): string {
  return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

function EditPencilIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M16.474 5.408l2.118 2.117m-.756-3.982L12.109 9.27a2.118 2.118 0 00-.58 1.082L11 13l2.648-.53c.41-.082.786-.283 1.082-.579l5.727-5.727a1.853 1.853 0 10-2.621-2.621z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M19 15v3a2 2 0 01-2 2H6a2 2 0 01-2-2V7a2 2 0 012-2h3"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function InlineEditForm(props: {
  initialContent: string;
  onSave: (newContent: string) => void;
  onCancel: () => void;
}) {
  const [editText, setEditText] = useState(props.initialContent);

  const handleSave = () => {
    const trimmed = editText.trim();

    if (!trimmed || trimmed === props.initialContent) {
      props.onCancel();
    } else {
      props.onSave(trimmed);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      props.onCancel();
    }
  };

  return (
    <div
      className={`${styles.editForm} flex flex-col rounded-[16px] border border-gray-200 bg-white transition-colors`}
      style={{ padding: '8px 4px' }}
    >
      <textarea
        className="w-full resize-none bg-transparent font-[inherit] text-[14px] leading-[1.5]"
        style={{ minHeight: '56px', maxHeight: '128px', padding: '8px 16px 12px' }}
        value={editText}
        onChange={(e) => setEditText(e.target.value)}
        onKeyDown={handleKeyDown}
        autoFocus
        data-testid="chat-edit-textarea"
        aria-label="Edit your message"
      />
      <div className="flex items-center justify-end gap-2" style={{ padding: '0 8px 4px' }}>
        <button
          type="button"
          className="bg-transparent border-none text-xs text-gray-500 cursor-pointer"
          onClick={props.onCancel}
          data-testid="chat-edit-cancel"
        >
          Cancel
        </button>
        <button
          type="button"
          className={`border-none rounded-full px-3 py-1.5 text-xs font-semibold ${
            !editText.trim()
              ? 'bg-gray-200 text-gray-400 cursor-default'
              : 'bg-gray-900 text-white cursor-pointer'
          }`}
          onClick={handleSave}
          disabled={!editText.trim()}
          data-testid="chat-edit-save"
        >
          Save & Submit
        </button>
      </div>
    </div>
  );
}

export default function MessageBubble(props: MessageBubbleProps) {
  const { message, isGrouped, isActiveToolCall } = props;
  const {
    isStreaming,
    editingMessageId,
    setEditingMessageId,
    editMessage,
  } = useChatWidgetContext();

  const isUser = message.role === 'user';
  const isToolActivity = message.role === 'tool-activity';
  const showAvatar = !isUser && !isGrouped;

  const canEdit = isUser && !!message.id && !isStreaming;
  const isEditing = editingMessageId === message.id;

  if (isToolActivity) {
    return (
      <div
        className={`${styles.messageRow} ${styles.messageRowAssistant} ${isGrouped ? styles.messageRowGrouped : ''}`}
        data-testid="chat-message-bubble-tool-activity"
      >
        {showAvatar ? (
          <div className={styles.bubbleAvatar}>
            <img src={nekstAiIcon} alt="Nekst AI" width={28} height={28} />
          </div>
        ) : (
          <div className={styles.bubbleAvatarSpacer} />
        )}
        <div className={styles.bubbleWrapper}>
          <div className={styles.toolActivityBubble}>
            <span className={isActiveToolCall ? styles.toolActivityIndicatorActive : styles.toolActivityIndicator} />
            {message.content}
          </div>
        </div>
      </div>
    );
  }

  const bubbleClassName = isUser
    ? styles.userBubble
    : message.isError
      ? styles.errorBubble
      : styles.assistantBubble;

  return (
    <div
      className={`${styles.messageRow} ${isUser ? styles.messageRowUser : styles.messageRowAssistant} ${!isUser && isGrouped ? styles.messageRowGrouped : ''} group`}
      data-testid={`chat-message-bubble-${message.role}`}
    >
      {!isUser && (
        showAvatar ? (
          <div className={styles.bubbleAvatar}>
            <img src={nekstAiIcon} alt="Nekst AI" width={28} height={28} />
          </div>
        ) : (
          <div className={styles.bubbleAvatarSpacer} />
        )
      )}
      <div className={styles.bubbleWrapper} style={isUser && isEditing ? { maxWidth: '100%', width: '100%' } : undefined}>
        {!isUser && !isGrouped && (
          <div className={styles.bubbleMeta}>
            <span className={styles.bubbleSender}>Nekst AI</span>
            <span className={styles.bubbleTime}>{formatTime(message.timestamp)}</span>
          </div>
        )}
        {isUser && isEditing ? (
          <InlineEditForm
            initialContent={message.content}
            onSave={(newContent) => editMessage(message.id!, newContent)}
            onCancel={() => setEditingMessageId(null)}
          />
        ) : (
          <div className={isUser ? 'flex items-center gap-1.5' : undefined}>
            <div className={bubbleClassName}>
              {isUser ? (
                message.content
              ) : (
                <ChatMarkdownContent content={message.content} />
              )}
            </div>
            {isUser && canEdit && (
              <button
                type="button"
                className="flex items-center justify-center w-[26px] h-[26px] p-0 bg-transparent border-none rounded-full text-gray-400 cursor-pointer shrink-0 opacity-0 group-hover:opacity-100 transition-opacity duration-150 hover:bg-gray-100 hover:text-gray-600"
                onClick={() => setEditingMessageId(message.id!)}
                data-testid="chat-edit-button"
                aria-label="Edit message"
              >
                <EditPencilIcon />
              </button>
            )}
          </div>
        )}
        {isUser && message.context && message.context.length > 0 && (
          <div className={styles.messageContextArea}>
            <ContextChipList context={message.context} />
          </div>
        )}
        {isUser && (
          <div className={`${styles.bubbleMeta} ${styles.bubbleMetaRight}`}>
            <span className={styles.bubbleTime}>You &bull; {formatTime(message.timestamp)}</span>
          </div>
        )}
      </div>
    </div>
  );
}
